import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Drawer,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useCouponUsagesQuery } from '../api';
import type { Coupon } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { formatCatalogPrice } from '@/features/products/format-price';

/**
 * OQ-25, décision 5 — l'historique complet (commande, client, date,
 * montant), pas un simple compteur. Chargé UNIQUEMENT quand ouvert
 * (`enabled: couponId !== undefined` dans `useCouponUsagesQuery`, gardé par
 * `coupon !== null` ici) — jamais préchargé pour toute la liste, pour
 * éviter une requête `usages` par coupon affiché.
 */
export function CouponUsageDrawer({
  coupon,
  onClose,
}: {
  coupon: Coupon | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  const usagesQuery = useCouponUsagesQuery(coupon?.id);

  return (
    <Drawer
      open={coupon !== null}
      title={t('merchant.promotions.usageDrawer.title', { code: coupon?.code ?? '' })}
      onClose={onClose}
    >
      {usagesQuery.isPending ? (
        <TableSkeleton columns={4} rows={4} />
      ) : usagesQuery.isError ? (
        <EmptyState
          title={t('merchant.promotions.usageDrawer.loadError')}
          description={t(resolveErrorMessageKey(usagesQuery.error))}
        />
      ) : usagesQuery.data.length === 0 ? (
        <EmptyState title={t('merchant.promotions.usageDrawer.neverUsed')} />
      ) : (
        <Table caption={t('merchant.promotions.usageDrawer.tableCaption')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('merchant.orders.table.colOrder')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.clients.table.colClient')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.promotions.usageDrawer.colDiscount')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.orders.table.colDate')}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usagesQuery.data.map((usage) => (
              <TableRow key={usage.id}>
                <TableCell>
                  <span className="font-mono text-sm">{usage.orderNumber}</span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{usage.customerName}</span>
                    <span className="text-xs text-inkFaint">{usage.customerPhone}</span>
                  </div>
                </TableCell>
                <TableCell>{formatCatalogPrice(usage.discountAmount)}</TableCell>
                <TableCell>{new Date(usage.createdAt).toLocaleString('fr-TN')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Drawer>
  );
}
